import React, { useEffect } from "react";
import { AudienceHeader } from "./AudienceChrome";
import SEO from "./SEO";
import "./TermsOfService.css";

/**
 * Terms of Service.
 *
 * Content is the 2026 revision, which covers both sides of the product: legal
 * professionals using the workspace, consumers using the free legal-question
 * tools, and the attorney/paralegal directory that sits between them. Keep this
 * in step with the source document rather than editing prose here piecemeal —
 * the numbering is referenced elsewhere.
 */
export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="terms-page">
      <SEO
        title="Terms of Service"
        path="/terms-of-service"
        description="LawFusion terms of service — the rules for using the platform, for legal professionals, consumers asking legal questions, and directory members."
      />
      <AudienceHeader />
      <main className="terms-wrapper">
        <section className="terms-card">
          <h1>📜 Terms of Service</h1>

          <p className="terms-last-updated">
            Effective Date: August 24, 2026 · Last Updated: August 26, 2026
          </p>

          <div className="terms-content">
            <section className="terms-section">
              <h2>Acceptance of Terms</h2>
              <p>
                By accessing or using the LawFusion.AI platform ("the Service"), you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access or use the Service. We reserve the right to modify these Terms at any time, and such modifications shall be effective immediately upon posting.
              </p>
            </section>

            <section className="terms-section">
              <h2>1) User Eligibility</h2>
              <p>
                You must be at least eighteen (18) years old to use the Service. By using the Service, you represent that you meet this age requirement. If you are using the Service on behalf of an employer, firm, or other organization, you represent that you are authorized to do so and to bind that organization to these Terms.
              </p>
            </section>

            <section className="terms-section">
              <h2>2) The Service; Consumer and Professional Use; No Legal Advice</h2>
              <p>
                LawFusion provides AI-assisted legal information, multi-model analysis, comparison tools, legal research features, and an attorney and paralegal directory, for use by both consumers and legal professionals.
              </p>
              <p>
                LawFusion is a technology platform and is not a law firm. Comparative AI Solutions LLC does not provide legal representation or legal advice and does not create an attorney-client relationship with any user, regardless of whether that user is a member of the public or a legal professional.
              </p>
              <p>
                <strong>Consumer use.</strong> Consumer-facing AI responses, summaries, comparisons, and other outputs are provided for general informational purposes only. They may discuss legal rules and how those rules could relate to facts provided by a user, but they are not a substitute for advice from a licensed attorney who has reviewed a user’s complete circumstances, and they do not guarantee any outcome.
              </p>
              <p>
                <strong>Professional use.</strong> Legal professionals using the Service remain independently responsible for their own professional judgment, for reviewing and verifying all information generated through the Service, and for compliance with their professional and ethical obligations.
              </p>
              <p>
                The Service is designed to help users compare information and identify inconsistencies among AI systems. It does not guarantee the accuracy or factual correctness of any output.
              </p>
            </section>

            <section className="terms-section">
              <h2>3) Acceptable Use; Submitted Information; Sensitive Data; IP in Inputs</h2>
              <p>You will use the Service lawfully and appropriately.</p>
              <p>
                <strong>Professional users</strong> who choose to upload materials that may contain privileged, confidential, or sensitive information (including client PII/PHI, financial account numbers, SSNs, attorney work product, or trade secrets) do so at their own risk and represent that they have the authority to share such materials with third-party AI processors for the purpose of legal analysis, and assume full responsibility for any consequences arising from that decision.
              </p>
              <p>
                <strong>Consumer users</strong> may submit legal questions, documents, correspondence, photographs, contracts, notices, or other materials. You should not submit information you do not have the legal right to provide.
              </p>
              <p>
                <strong>For all users:</strong> the Service is not an attorney, law firm, or privileged communication channel. Submitting information to LawFusion does not, by itself, create attorney-client privilege or any other legal or professional privilege.
              </p>
              <p>
                You retain ownership of all intellectual property rights in your submitted inputs and data. By submitting content, you grant Comparative AI Solutions LLC a non-exclusive, worldwide, royalty-free license to process and display that content solely to operate and improve the Service.
              </p>
            </section>

            <section className="terms-section">
              <h2>4) AI Outputs; Your Responsibility</h2>
              <p>
                AI outputs may contain errors, omissions, outdated information, incorrect citations, conflicting interpretations, or fabricated or hallucinated information. Different AI models may reach different conclusions on the same question.
              </p>
              <p>
                Where the Service generates a summary or synthesis from multiple underlying AI analyses, that summary should not be interpreted as independent verification that any particular AI response, legal authority, deadline, conclusion, or interpretation is correct. A summary reflects and compresses the underlying analyses; it does not independently confirm their accuracy.
              </p>
              <p>
                LawFusion is not responsible for false, incomplete, or hallucinated content generated by any AI system. Users are solely responsible for independently fact-checking, verifying, and confirming the accuracy of any AI-generated content before relying on it or taking or refraining from any legal, financial, procedural, or other action based upon it.
              </p>
            </section>

            <section className="terms-section">
              <h2>5) Attorney and Paralegal Directory</h2>
              <p>
                LawFusion operates a directory through which attorneys, paralegals, and other permitted legal professionals may submit information about themselves and their services.
              </p>
              <p>
                Directory information is supplied primarily by the person or organization creating the listing. Comparative AI Solutions LLC does not independently verify the accuracy, completeness, currency, qualifications, experience, practice areas, licensing status, disciplinary history, professional standing, or other claims contained in a listing, unless expressly stated otherwise.
              </p>
              <p>
                A <strong>"Listing Verified"</strong> designation means only that the email address associated with the listing was confirmed through LawFusion’s verification process at signup. It does not mean that LawFusion has verified the person’s identity, bar membership, license status, credentials, disciplinary record, competence, experience, practice areas, phone number, website, or any other information contained in the listing.
              </p>
              <p>
                A <strong>"Featured"</strong> or sponsored designation relates to visibility, placement, advertising, or promotional status only. It does not constitute verification, endorsement, recommendation, certification, or approval by LawFusion.
              </p>
              <p>
                Users should independently investigate any attorney, paralegal, firm, or other professional before contacting, hiring, retaining, or relying upon that person — including confirming licensing and disciplinary information directly with the applicable state bar or other licensing authority.
              </p>
              <p>
                LawFusion does not recommend, endorse, guarantee, or warrant any directory member, and is not responsible for the statements, omissions, conduct, services, fees, advice, representation, results, or other acts of any person listed in the directory.
              </p>
              <p>
                Contacting a professional through, or as a result of using, LawFusion does not itself create an attorney-client relationship. Any attorney-client or other professional relationship exists solely between the user and the professional they choose to engage.
              </p>
              <p>
                A paralegal listing does not represent or imply that the listed individual is an attorney or is independently authorized to practice law or provide legal advice. Users are responsible for determining what services a paralegal may lawfully provide in the applicable jurisdiction.
              </p>
              <p>
                LawFusion does not act as legal counsel or as a party to any professional engagement between users and directory members, does not supervise or control the services they provide, and does not participate in or determine their professional fees. Inclusion or placement in the directory should not be interpreted as a referral or recommendation by LawFusion.
              </p>
              <p>
                LawFusion does not undertake any duty to investigate, monitor, reverify, or continuously update the information contained in directory listings.
              </p>
              <p>
                Users access and rely upon directory information at their own risk and are solely responsible for determining whether any listed professional is properly licensed, qualified, and appropriate for their needs.
              </p>
            </section>

            <section className="terms-section">
              <h2>6) Directory Member Representations</h2>
              <p>
                If you create or maintain a directory listing, you represent and warrant that:
              </p>
              <ul>
                <li>all information you submit is truthful, accurate, current, and not misleading;</li>
                <li>you have the authority to submit and publish that information;</li>
                <li>your listing complies with all applicable laws, professional rules, advertising rules, licensing requirements, and ethical obligations that apply to you.</li>
              </ul>
              <p>
                You are responsible for promptly updating information that becomes inaccurate or outdated. LawFusion may edit, suspend, remove, or refuse any listing at its discretion, including listings we reasonably believe contain inaccurate, misleading, unlawful, or inappropriate information.
              </p>
              <p>
                You may not falsely claim licensure, credentials, experience, specialization, professional status, or affiliation.
              </p>
              <p>
                If you purchase, submit, or maintain advertising, sponsored, featured, or promotional content through LawFusion, you represent and warrant that the content is truthful, accurate, not misleading, that you have all rights and permissions necessary to publish it, and that it complies with all applicable laws, professional-conduct rules, attorney-advertising rules, licensing requirements, and ethical obligations. LawFusion may reject, modify, suspend, or remove advertising content that it reasonably believes violates these requirements or is otherwise inappropriate for the Service.
              </p>
            </section>

            <section className="terms-section">
              <h2>7) Accounts, Plans, Anonymous Use &amp; Changes</h2>
              <p>
                Certain portions of the Service may be available without creating an account. We may impose usage limits, rate limits, or other restrictions on anonymous or free access, and may require account creation to continue using certain features.
              </p>
              <p>
                We may change features, plans, or pricing and may suspend or terminate accounts for misuse or nonpayment.
              </p>

              <h3>7A) Billing, Refunds &amp; Cancellations</h3>
              <p>
                Paid subscriptions, advertising placements, and other recurring paid products renew automatically unless canceled before the next billing cycle, unless otherwise stated at purchase. You may cancel anytime via your account dashboard or by contacting <a href="mailto:support@lawfusion.ai">support@lawfusion.ai</a>. Refunds and money-back guarantees, if offered, will be governed by the refund or guarantee terms presented at the time of purchase. Except where a specific guarantee applies or applicable law requires otherwise, fees for subscriptions, advertising placements, and other recurring paid products are non-refundable once charged. We may suspend or terminate for nonpayment or misuse.
              </p>

              <h3>7B) Data Portability</h3>
              <p>
                You may request an export of your stored User Content, where technically feasible, prior to account deletion. Exports may exclude AI-generated metadata or internal analytics.
              </p>
            </section>

            <section className="terms-section">
              <h2>8) Security; Incidents</h2>
              <p>
                We use reasonable safeguards but do not guarantee the Service will be free from vulnerabilities, malware, or unauthorized access. If an incident occurs, we will notify you where legally required.
              </p>
            </section>

            <section className="terms-section">
              <h2>9) Third-Party Services</h2>
              <p>
                The Service integrates with third-party AI and other providers. Those services are not under our control. We disclaim responsibility for their acts, omissions, outages, or content.
              </p>
            </section>

            <section className="terms-section">
              <h2>10) Beta &amp; Trial Features</h2>
              <p>
                Features labeled beta or trial are provided AS IS and may be changed or removed at any time.
              </p>
            </section>

            <section className="terms-section">
              <h2>11) Sponsored &amp; Advertising Content</h2>
              <p>
                LawFusion may display sponsored, promoted, featured, or paid placements from attorneys, paralegals, firms, or other advertisers.
              </p>
              <p>
                Payment for visibility does not constitute an endorsement, recommendation, verification of credentials, or a determination that the advertiser is appropriate for any particular user’s legal matter. Sponsored or featured status is unrelated to, and does not affect, any "Listing Verified" designation.
              </p>
              <p>
                <strong>Advertising Placement Terms.</strong> Advertising placements, including any exclusive statewide banner placement, are offered on a month-to-month basis unless otherwise expressly stated at the time of purchase. Any exclusivity applies only during the advertiser’s then-current paid term. LawFusion may change advertising pricing, placement locations, availability, targeting, exclusivity arrangements, or the number of advertising placements offered in any state for future billing periods. Any such change will not reduce or alter exclusivity already purchased for the advertiser’s current paid term.
              </p>
              <p>
                <strong>Automated Matching and Targeting.</strong> LawFusion may use information provided by a consumer, including selected state and the general subject matter of a legal question, to determine which sponsored, featured, or advertising placements are displayed. Any such matching or targeting is automated and is provided for advertising and directory-display purposes only. It does not constitute a referral, recommendation, endorsement, determination of suitability, or representation that any attorney, paralegal, firm, or advertiser is qualified or appropriate for a particular user’s legal matter. Automated classifications or matches may be incomplete or inaccurate.
              </p>
              <p>
                <strong>No Traffic or Performance Guarantee.</strong> Unless LawFusion expressly agrees otherwise in writing at the time of purchase, advertising or featured placement does not guarantee any minimum number of impressions, views, clicks, inquiries, leads, clients, conversions, or other results. Traffic and user activity may vary by state, practice area, time period, and other factors.
              </p>
            </section>

            <section className="terms-section">
              <h2>12) Intellectual Property; Inputs/Outputs; Overlap</h2>
              <p>
                The Service (UI, code, workflows, documentation) is owned by Comparative AI Solutions LLC and its licensors. We do not claim ownership of your inputs or outputs. Due to the probabilistic nature of AI, similar or identical outputs may be generated for other users. Comparative AI Solutions LLC disclaims responsibility for third-party content or potential intellectual property overlaps in AI-generated material.
              </p>
            </section>

            <section className="terms-section">
              <h2>13) Disclaimers</h2>
              <p>
                To the fullest extent permitted by law, the Service and all outputs are provided AS IS and AS AVAILABLE, without warranties of any kind, whether express, implied, or statutory, including merchantability, fitness for a particular purpose, accuracy, non-infringement, or that outputs will meet your needs.
              </p>
              <p>
                We make no representations or warranties regarding the accuracy, completeness, or reliability of any AI-generated information, and all outputs should be independently verified.
              </p>
              <p>
                We make no warranty regarding any directory member, listing, advertisement, sponsored placement, professional service, or interaction resulting from use of the directory.
              </p>
            </section>

            <section className="terms-section">
              <h2>14) Limitation of Liability; Exclusive Remedy</h2>
              <p>
                No indirect, incidental, consequential, special, exemplary, or punitive damages; no loss of profits, revenue, data, use, goodwill; no business interruption or replacement costs.
              </p>
              <p>
                To the fullest extent permitted by applicable law, Comparative AI Solutions LLC will not be liable for the acts, omissions, advice, malpractice, misconduct, representations, fees, services, or outcomes involving any attorney, paralegal, law firm, advertiser, or other third party located through the Service.
              </p>
              <p>
                Total aggregate liability will not exceed the fees you paid in the single (1) month immediately preceding the event giving rise to the claim (or USD $50 for free, anonymous, or beta users). Your sole remedy is to stop using the Service and, if applicable, seek a refund up to the cap above where legally required.
              </p>
            </section>

            <section className="terms-section">
              <h2>15) Indemnification</h2>
              <p>
                You agree to defend, indemnify, and hold harmless Comparative AI Solutions LLC, its affiliates, officers, employees, and agents from and against claims, liabilities, damages, losses, costs, and expenses, including reasonable attorneys’ fees, arising out of or relating to your use of the Service, your submitted content, your breach of these Terms, or your violation of applicable law.
              </p>
              <p>
                If you create or maintain a directory listing, this obligation specifically includes claims arising from inaccurate, false, misleading, outdated, unauthorized, or unlawful information you submit or publish through your listing, including claims concerning licensure, credentials, experience, practice areas, professional status, or services.
              </p>
              <p>
                If you purchase or provide advertising, sponsored, featured, or promotional content, this indemnification obligation also includes claims arising from false, misleading, unlawful, unauthorized, or noncompliant advertising content, including claims relating to professional credentials, specialization, licensing, legal services, advertising claims, or applicable professional-conduct rules.
              </p>
            </section>

            <section className="terms-section">
              <h2>16) Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of California, without regard to conflict of laws rules.
              </p>
            </section>

            <section className="terms-section">
              <h2>17) Arbitration; No Class; Small Claims; Injunctive Relief; Limitations</h2>
              <p>
                All disputes arising out of or relating to these Terms or the Services shall be resolved exclusively by binding arbitration administered by JAMS or AAA in Solano County, Northern California, under their Comprehensive Arbitration Rules. The arbitrator has exclusive authority to resolve any disputes relating to arbitrability. Each party bears its own attorneys’ fees and costs, and arbitration fees are allocated equally unless the arbitrator determines otherwise based on applicable law or the merits (in no event shall a consumer be required to pay the non-consumer party’s fees or costs). Either party may bring an individual action in small claims court within jurisdictional limits. Either party may seek temporary or preliminary injunctive relief in a court of competent jurisdiction to protect rights pending arbitration. Any claim must be filed within one (1) year of the event giving rise to it, unless prohibited by law. No class or representative actions are permitted; claims must be brought individually. Judgment on the award may be entered in any court having jurisdiction.
              </p>
            </section>

            <section className="terms-section">
              <h2>18) Force Majeure</h2>
              <p>
                We are not liable for delays or failures caused by events beyond our reasonable control (for example, internet outages, DDoS, power failures, labor disputes, terrorism, cyberattacks, natural disasters, or changes in law).
              </p>
            </section>

            <section className="terms-section">
              <h2>19) Severability; Waiver; Assignment; Successors</h2>
              <p>
                If any provision is unenforceable, it will be limited or removed to the minimum extent necessary, and the remainder will remain in full force and effect. Our failure to enforce a provision is not a waiver. You may not assign these Terms without our prior written consent. We may assign to an affiliate or in connection with a merger, sale, or similar transaction. These Terms bind permitted successors and assigns.
              </p>
            </section>

            <section className="terms-section">
              <h2>20) Termination &amp; Suspension</h2>
              <p>
                We may suspend or terminate your access to the Service at any time for violation of these Terms, nonpayment, or technical or security reasons. Upon termination, your access to stored data and outputs may be removed. You may export or back up your data before closure, as we do not guarantee post-termination retention.
              </p>
            </section>

            <section className="terms-section">
              <h2>21) Accuracy &amp; Responsibility</h2>
              <p>
                The platform is designed to assist users in comparing AI-generated information to identify inconsistencies and potential errors. While this may help reduce hallucinations or improve result quality, Comparative AI Solutions LLC does not guarantee accuracy. Users are solely responsible for reviewing and verifying all information before use.
              </p>
            </section>

            <section className="terms-section">
              <h2>22) Notices of Changes</h2>
              <p>
                We may modify these Terms by posting a revised version with a new effective date; continued use after that date constitutes acceptance.
              </p>
            </section>

            <section className="terms-section">
              <h2>23) Contact</h2>
              <p>
                <strong>Comparative AI Solutions LLC (Owner of LawFusion.AI)</strong><br />
                Vacaville, California, USA<br />
                <strong>Email:</strong>{" "}
                <a href="mailto:support@lawfusion.ai">support@lawfusion.ai</a>
              </p>
            </section>
          </div>
        </section>
      </main>
    </div>
  );
}
